import AssetsTable from 'atoms/AssetsTable'
import React from 'react'
import { useMemo, useState, useContext } from 'react'

import { CallToActionRow, AccountRow } from 'atoms/AssetsTable'
import { Box, Typography } from '@mui/material'

import { IntakeContext } from 'context/IntakeContext'

import { useModal } from 'components/modals'
import { selectData } from 'layouts/onboarding'

const dollarFormat = Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
})

function NetWorthTable({
  getAccounts,
  assets,
  liabilities,
  demoMode = false,
  isAdmin = false,
}) {
  //handle calculations here to build the rows necessary for the retirement gap,
  //ensure they are memoized so they dont re-render unless the user changes to-do list items

  const { intakeState } = useContext(IntakeContext)
  const isGovernmentEmployee = intakeState?.governmentEmployee
  const { openModal } = useModal()

  const [open, setOpen] = useState(false)

  const handleClick = (callback) => {
    if (demoMode) return undefined
    return callback
  }

  const hasThriftSavingsPlan = assets?.some(
    (item) =>
      item.externalAsset === true &&
      item.externalInstitutionName === 'Thrift Savings Plan'
  )

  const assetSum =
    (assets || []).map((assets) => assets.amount).reduce((a, b) => a + b, 0) +
    (hasThriftSavingsPlan
      ? 0
      : (intakeState.tspBalance ? intakeState.tspBalance : 0) +
        (intakeState.tspRothBalance ? intakeState.tspRothBalance : 0))

  const liabilitySum = (liabilities || [])
    .map((liabilities) => liabilities.amount)
    .reduce((a, b) => a + b, 0)

  const netWorth = assetSum - liabilitySum

  const rows = useMemo(() => {
    const safeAssets = assets || []
    const safeLiabilities = liabilities || []
    // console.log(safeAssets)

    const baseRows = [
      {
        header: 'Assets',
        headerValue: assetSum,
        expandable: true,
        addAccountRow: demoMode ? undefined : 'Asset',
        content: (
          <>
            {!hasThriftSavingsPlan && isGovernmentEmployee && (
              // <CallToActionRow text="Add my TSP Account" />
              <>
                {(intakeState?.tspRothBalance || 0) > 0 && (
                  <AccountRow
                    onClick={handleClick(() =>
                      openModal('fieldModal', {
                        title: `Let's update your Thrift Savings Plan (Roth) Balance!`,
                        userField: 'tspRothBalance',
                        inputType: 'currencyFloat',
                        fieldLabel: 'Thrift Savings Plan (Roth) Balance',
                        quilttConnect: true,
                        getAccounts: getAccounts,
                      })
                    )}
                    key={'rothTSP'}
                    name={'Thrift Savings Plan (Roth)'}
                    amount={intakeState?.tspRothBalance || 0}
                    img={
                      'https://upload.wikimedia.org/wikipedia/commons/b/b6/US-ThriftSavingsPlan-Logo.svg'
                    }
                  />
                )}
                {(intakeState?.tspBalance || 0) > 0 && (
                  <AccountRow
                    onClick={handleClick(() =>
                      openModal('fieldModal', {
                        title: `Let's update your Thrift Savings Plan (Traditional) Balance!`,
                        userField: 'tspBalance',
                        inputType: 'currencyFloat',
                        fieldLabel: 'Thrift Savings Plan (Traditional) Balance',
                        quilttConnect: true,
                        getAccounts: getAccounts,
                      })
                    )}
                    key={'tradTSP'}
                    name={'Thrift Savings Plan (Traditional)'}
                    amount={intakeState?.tspBalance || 0}
                    img={
                      'https://upload.wikimedia.org/wikipedia/commons/b/b6/US-ThriftSavingsPlan-Logo.svg'
                    }
                  />
                )}
              </>
            )}

            {safeAssets.map((asset, index) => (
              <AccountRow
                onClick={handleClick(() =>
                  openModal('editAccount', {
                    accountType: 'Asset',
                    account: asset,
                    getAccounts: getAccounts,
                    isAdmin: isAdmin,
                  })
                )}
                key={index}
                name={asset.name || 'Unnamed Account'}
                amount={asset.amount || 0}
                img={
                  asset?.policyNumber
                    ? selectData.annuityCarriers.find(
                        ({ value }) => value === asset.carrier
                      )?.tableLogo || ''
                    : asset.externalInstitutionLogo
                }
                subType={asset.subType}
                linked={asset?.policyNumber ? true : asset?.externalAsset}
                signalLinked={asset?.policyNumber ? true : false}
              />
            ))}
          </>
        ),
      },
      {
        header: 'Liabilities',
        headerValue: liabilitySum,
        expandable: true,
        addAccountRow: demoMode ? undefined : 'Liability',
        content:
          safeLiabilities.length == 0 && !demoMode ? (
            <>
              <CallToActionRow
                text="Link a Liability to track"
                onClick={handleClick(() =>
                  openModal('addAccounts', {
                    getAccounts: getAccounts,
                    openToPage: 'Liability',
                  })
                )}
              />
            </>
          ) : (
            <>
              {safeLiabilities.map((liability, index) => (
                <AccountRow
                  onClick={handleClick(() =>
                    openModal('editAccount', {
                      accountType: 'Liability',
                      account: liability,
                      getAccounts: getAccounts,
                    })
                  )}
                  key={index}
                  name={liability.name || 'Unnamed Account'}
                  amount={liability.amount || 0}
                  img={liability.externalInstitutionLogo}
                  subType={liability.subType}
                  linked={liability?.externalAsset}
                />
              ))}
            </>
          ),
      },
    ]
    if (isAdmin) {
      baseRows.push({
        header: 'Net Worth',
        headerValue: netWorth,
        expandable: true,
        // addAccountRow: 'Liability',
        content: (
          <Typography textAlign="center" color="#262627" pt={1}>
            Total Assets - Total Liabilities<br></br>
            {dollarFormat.format(assetSum)} -{' '}
            {dollarFormat.format(liabilitySum)}
          </Typography>
        ),
      })
    }
    return baseRows
  }, [assets, liabilities, intakeState, isAdmin])

  return (
    <>
      <AssetsTable
        getAccounts={getAccounts}
        rows={rows}
        color={demoMode ? 'gray' : 'blue'}
        demoMode={demoMode}
        isAdmin={isAdmin}
      />
    </>
  )
}

export default NetWorthTable
